import { apiFetch } from '../../../lib/apiFetch';
import AppConfig from '../../../lib/AppConfig';
import { cleanApiError, handleResponse } from '../../../utils/handleResponse';
import { buildQuery, getAPIHeaders, QueryParams } from '../../../utils/helpers';
import { ConvertCreateClientResponse } from '../../Members/types/response/CreateClient';
import { CreateLoanRequest } from '../types/request/CreateLoan';
import { DataTableRequest } from '../types/request/DataTableRequest';
import { ListLoansRequest, ListLoansRequestLite } from '../types/request/ListLoans';
import { TLoanApplicationRequest } from '../types/request/LoanApplication';
import { TApproveLoan, TDisburseLoan, TRejectLoan } from '../types/request/Loans';
import { TLoanApplicationTemplate } from '../types/response/ApplicationTemplate';
import { ClientLoan } from '../types/response/ClientLoan';
import { CreateLoanResponse } from '../types/response/CreateLoan';
import { TAPILoan } from '../types/response/GetLoan';
import { ListLoansResponse } from '../types/response/ListLoans';
import { ConvertLoanProductResponse, LoanProduct } from '../types/response/LoanProduct';
import { ConvertLoanProductsResponse, LoanProductsResponse } from '../types/response/LoanProducts';
import { LoanResponseLite } from '../types/response/LoanResponseLite';
import { ConvertLoanTemplate, LoanTemplate } from '../types/response/LoanTemplate';
import { TLoanTransactionTemplateResponse } from '../types/response/LoanTxTemplate';
import { ListLoanPayoffResponse } from '../types/response/Payoff';
import { LoanProductTemplate } from '../types/response/ProductTemplate';
import { RepaymentResponse } from '../types/response/Repayment';
import { ScoreResponse } from '../types/response/Score';
import { LoanSummary } from '../types/response/Summary';

export namespace LoanRepository {
	// listLoans
	export async function listLoans(request: ListLoansRequest): Promise<ListLoansResponse> {
		// Implementation can change
		try {
			const endPoint = `/fineract-provider/api/v1/loans`;
			const pagination = `${request.offset ? `offset=${request.offset}` : ''}${
				request.limit ? `&limit=${request.limit}` : ''
			}`;
			// const orderBy = `&orderBy=${request.orderBy ?? 'accountNo'}`;
			// const sorttBy = `&sortBy=${request.sortBy ?? 'DESC'}`;
			const url = `${AppConfig.api_url}${endPoint}?${pagination}`;

			const response = await fetch(url, {
				headers: await getAPIHeaders(),
			});

			const data = await handleResponse(response);

			return data;
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// listLoanLite
	export async function listLoanLite(
		request: ListLoansRequestLite = {},
	): Promise<LoanResponseLite> {
		try {
			const qs = buildQuery(request as QueryParams);
			const url = `${AppConfig.api_url}/gmapis/loans${qs ? `?${qs}` : ''}`;
			const response = await fetch(url.toString(), {
				headers: await getAPIHeaders(),
			});
			const data = await handleResponse(response);
			return data;
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	export async function getLoanSummary(params: Record<string, any>): Promise<LoanSummary> {
		const qs = buildQuery(params);
		const url = `${AppConfig.api_url}/gmapis/loans/summary${qs ? `?${qs}` : ''}`;
		const res = await fetch(url, { headers: await getAPIHeaders() });
		return handleResponse(res);
	}

	// listLoanProducts
	export async function listLoanProducts(): Promise<LoanProductsResponse> {
		// Implementation can change
		try {
			const endPoint = `/fineract-provider/api/v1/loanproducts/template?isProductMixTemplate=true`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const response = await fetch(url, {
				headers: await getAPIHeaders(),
			});

			const data = await handleResponse(response);

			return ConvertLoanProductsResponse.toLoanProductsResponse(data);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// listLoanProductTemplate
	export async function listLoanProductTemplate(productId: number): Promise<LoanProductTemplate> {
		// Implementation can change
		try {
			const endPoint = `/fineract-provider/api/v1/loanproducts/${productId}?template=true`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const response = await fetch(url, {
				headers: await getAPIHeaders(),
			});

			const data = await handleResponse(response);

			return data;
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// listLoanTemplate
	export async function listLoanTemplates(): Promise<LoanTemplate> {
		// Implementation can change
		try {
			const endPoint = `/fineract-provider/api/v1/loanproducts/template`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const response = await fetch(url, {
				headers: await getAPIHeaders(),
			});

			const data = await handleResponse(response);

			return ConvertLoanTemplate.toLoanTemplate(data);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// getLoanProduct
	export async function getLoanProduct(productId: string | number): Promise<LoanProduct> {
		// Implementation can change
		try {
			const endPoint = `/fineract-provider/api/v1/loanproducts/${productId}`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const response = await fetch(url, {
				headers: await getAPIHeaders(),
			});

			const data = await handleResponse(response);

			return ConvertLoanProductResponse.toLoanProduct(data);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// calculateLoanRepayment
	export async function calculateLoanRepayment(
		request: CreateLoanRequest,
	): Promise<RepaymentResponse> {
		// Implementation can change
		try {
			const endPoint = `/fineract-provider/api/v1/loans?command=calculateLoanSchedule`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				body: JSON.stringify(request),
				headers,
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// createLoan
	export async function createLoan(request: CreateLoanRequest): Promise<CreateLoanResponse> {
		// Implementation can change
		try {
			const endPoint = `/fineract-provider/api/v1/loans`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				body: JSON.stringify(request),
				headers,
			});

			const data = await handleResponse(response);

			return ConvertCreateClientResponse.toCreateClientResponse(data);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// getClientLoans
	export async function getClientLoans(clientId: string | number): Promise<ClientLoan[]> {
		// Implementation can change
		try {
			const endPoint = `/fineract-provider/api/v1/runreports/dashboard_active_loan_client_id?genericResultSet=false&R_clientId=${clientId}`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const response = await fetch(url, {
				headers: await getAPIHeaders(),
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// getLoan
	export async function getLoan(loanId: string | number): Promise<TAPILoan> {
		// Implementation can change
		try {
			const endPoint = `/fineract-provider/api/v1/loans/${loanId}?associations=all`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const response = await fetch(url, {
				headers: await getAPIHeaders(),
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// addNote
	export async function addNote(loanId: string | number, notes: string): Promise<void> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/loans/${loanId}/notes`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				body: JSON.stringify({
					note: notes,
				}),
				headers,
			});

			await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// updateDataTableEntry
	export async function updateDataTableEntry<T>(request: DataTableRequest<T>): Promise<void> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/datatables/${request.dataTable}/${request.loanId}`;
			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'PUT',
				body: JSON.stringify(request.data),
				headers,
			});

			await handleResponse(response);
		} catch (error: any) {
			if (
				error?.httpStatusCode === '404' ||
				error?.errors?.some(
					(err: any) => err?.userMessageGlobalisationCode === 'error.msg.datatable.data.not.found',
				)
			) {
				console.log('Data not found for datatable, falling back to addDataTableEntry.');
				await addDataTableEntry(request);
			} else {
				throw cleanApiError(error);
			}
		}
	}

	// addDataTableEntry
	export async function addDataTableEntry<T>(request: DataTableRequest<T>): Promise<void> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/datatables/${request.dataTable}/${request.loanId}`;
			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				body: JSON.stringify(request.data),
				headers,
			});

			await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// getLoanApplicationTemplate ~ retrieves loan application template
	export async function getLoanApplicationTemplate(
		request: TLoanApplicationRequest,
	): Promise<TLoanApplicationTemplate> {
		const endPoint = `/fineract-provider/api/v1/loans/template`;
		const query = new URLSearchParams(buildQuery(request)).toString();
		const url = `${AppConfig.api_url}${endPoint}${query ? `?${query}` : ''}`;
		return apiFetch(url);
	}

	// approveLoan
	export async function approveLoan(loanId: number, req: TApproveLoan): Promise<any> {
		// Implementation can change
		try {
			const endPoint = `/fineract-provider/api/v1/loans/${loanId}?command=approve`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify(req),
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// rejectLoan
	export async function rejectLoan(loanId: number, req: TRejectLoan): Promise<any> {
		// Implementation can change
		try {
			const endPoint = `/fineract-provider/api/v1/loans/${loanId}?command=reject`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify(req),
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// getDataTableEntry
	export async function getDataTableEntry<T>(request: DataTableRequest<T>): Promise<T[]> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/datatables/${request.dataTable}/${request.loanId}?genericResultSet=false`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, { headers });

			const data = await handleResponse(response);

			return data;
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// getDataTableEntry
	export async function getLoanTransactionTemplate(
		loanId: number,
		command: string,
		params?: Record<string, string>,
	): Promise<TLoanTransactionTemplateResponse> {
		try {
			const queryParams = new URLSearchParams({ command, ...(params || {}) }).toString();
			const url =
				`${AppConfig.api_url}/fineract-provider/api/v1/loans/${loanId}/transactions/template?${queryParams}`.replace(
					/\+/g,
					' ',
				);

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, { headers });

			const data = await handleResponse(response);

			return data;
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// disburseLoan
	export async function disburseLoan(loanId: number, req: TDisburseLoan): Promise<any> {
		// Implementation can change
		try {
			const endPoint = `/fineract-provider/api/v1/loans/${loanId}?command=disburse`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify(req),
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// disburseLoanToSavings
	export async function disburseLoanToSavings(loanId: number, req: TDisburseLoan): Promise<any> {
		// Implementation can change
		try {
			const endPoint = `/fineract-provider/api/v1/loans/${loanId}?command=disburseToSavings`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify(req),
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// getScore
	export async function getScore(uniqueIdentifier: string): Promise<ScoreResponse> {
		try {
			const url = `${AppConfig.api_url}/gmapis/get_score/${uniqueIdentifier}`;

			const response = await fetch(url, {
				headers: await getAPIHeaders(),
			});

			const data = await handleResponse(response);

			return data;
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// Define Payoff type
	export interface ReqeqLoanPayoff {
		client_id?: number;
		loan_id: number;
		amount: number;
	}

	//  addPayoff function
	export async function addPayoff(body?: ReqeqLoanPayoff[]): Promise<any> {
		try {
			const endPoint = `/gmapis/loans/payoffs`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				headers: headers,
				method: 'POST',
				body: JSON.stringify(body),
			});

			const data = await handleResponse(response);
			return data;
		} catch (error: any) {
			console.error('Request failed:', error);
			throw cleanApiError(error);
		}
	}

	//  listPayoffs function
	export async function listPayoffs(loanId: number): Promise<ListLoanPayoffResponse> {
		try {
			const endPoint = `/gmapis/loans/payoffs?loan_id=${loanId}`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();

			const response = await fetch(url, {
				headers: headers,
			});

			const data = await handleResponse(response);
			return data;
		} catch (error: any) {
			console.error('Request failed:', error);
			throw cleanApiError(error);
		}
	}

	//  forecloseLoan function
	export async function forecloseLoan(loanId: number, req: Record<string, any>): Promise<any> {
		try {
			const endPoint = `/fineract-provider/api/v1/loans/${loanId}/transactions?command=foreclosure`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify(req),
			});

			return await handleResponse(response);
		} catch (error: any) {
			console.error('Request failed:', error);
			throw cleanApiError(error);
		}
	}

	// makeRepayment
	export async function makeRepayment(loanId: number, req: Record<string, any>): Promise<any> {
		// Implementation can change
		try {
			const endPoint = `/fineract-provider/api/v1/loans/${loanId}/transactions?command=repayment`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify(req),
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// undoDisbursal
	export async function undoDisbursal(glimAccId: number, note: string): Promise<any> {
		// Implementation can change
		try {
			const endPoint = `/fineract-provider/api/v1/loans/${glimAccId}?command=undodisbursal`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify({ note }),
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// prepayLoan
	export async function prepayLoan(loanId: number, req: Record<string, any>): Promise<any> {
		// Implementation can change
		try {
			const endPoint = `/fineract-provider/api/v1/loans/${loanId}/transactions?command=repayment`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify(req),
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}
}
